import type { ReactNode } from "react";
import { css, cx } from "@strust/ui/css";
import { topbar } from "@strust/ui/recipes";
import { MenuButton } from "./navigation.tsx";

const INTERACTIVE = css({
  cursor: "pointer",
  _hover: { borderColor: "accent.line", color: "text" },
  _focusVisible: { outline: "none", boxShadow: "focus" },
  "&[aria-expanded=true]": { borderColor: "accent", color: "text" },
});
const ICON = css({ display: "inline-flex" });

function chipClass(): string {
  return cx(topbar().chip, INTERACTIVE);
}

export interface TopbarMenuChipProps {
  /** Accessible name; it contains the visible text (e.g. "Language: PT-BR"). */
  readonly label: string;
  readonly icon?: ReactNode;
  /** Visible text of the chip (e.g. the locale code `PT-BR`). */
  readonly text: string;
  /** Items of the menu: buttons or links styled with `menuClasses.item`. */
  readonly children: ReactNode;
}

/** Compact top-bar chip that opens a menu (language): icon and a short mono text. */
export function TopbarMenuChip({ label, icon, text, children }: TopbarMenuChipProps) {
  return (
    <MenuButton
      label={label}
      triggerClassName={chipClass()}
      closeOnSelect
      trigger={
        <>
          {icon ? (
            <span aria-hidden="true" className={ICON}>
              {icon}
            </span>
          ) : null}
          <span aria-hidden="true">{text}</span>
        </>
      }
    >
      {children}
    </MenuButton>
  );
}

export interface TopbarToggleChipProps {
  /** Accessible name; it contains the visible text (e.g. "Theme: Dark"). */
  readonly label: string;
  readonly icon?: ReactNode;
  readonly text: string;
  readonly onClick: () => void;
}

/** Top-bar chip that switches a two-state setting in place (theme): icon and the current state. */
export function TopbarToggleChip({ label, icon, text, onClick }: TopbarToggleChipProps) {
  return (
    <button
      type="button"
      className={chipClass()}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      {icon ? (
        <span aria-hidden="true" className={ICON}>
          {icon}
        </span>
      ) : null}
      <span aria-hidden="true">{text}</span>
    </button>
  );
}
